import React, { useState, useMemo, useCallback } from 'react';
import { getAssetUrl } from '../../utils/assetUrl';
import { CmsButton } from '../../types/contentstack';
import LinkIcon from '../icons/LinkIcon';

interface CmsJoinSectionStat {
  heading: string;
  body: string;
  image?: {
    url: string;
    description?: string;
  };
  author_name?: string;
  author_title?: string;
  buttons?: CmsButton[];
}

interface CmsJoinSectionData {
  name?: string;
  component?: string;
  heading?: string;
  sub_heading?: string;
  features: CmsJoinSectionStat[];
  background_color?: string;
}

interface CmsJoinSectionProps {
  data: CmsJoinSectionData;
  className?: string;
}

const ChevronLeftIcon = () => (
  <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export const CmsJoinSection: React.FC<CmsJoinSectionProps> = ({ data, className = '' }) => {
  const quoteIcon = getAssetUrl('unauth/testimonial_quote.png');
  const [currentSlide, setCurrentSlide] = useState(0);

  const testimonials = useMemo(
    () => data.features?.filter((f) => f.heading) ?? [],
    [data.features]
  );

  const { hasMultipleTestimonials, maxSlideIndex, totalPages } = useMemo(() => {
    const hasMultiple = testimonials.length > 3;
    const maxIndex = testimonials.length - 3;
    return {
      hasMultipleTestimonials: hasMultiple,
      maxSlideIndex: maxIndex,
      totalPages: hasMultiple ? maxIndex + 1 : 0,
    };
  }, [testimonials.length]);

  const isFirstPage = currentSlide === 0;
  const isLastPage = currentSlide >= maxSlideIndex;

  const goToNext = useCallback(() => {
    if (!isLastPage) {
      setCurrentSlide((prev) => prev + 1);
    }
  }, [isLastPage]);

  const goToPrev = useCallback(() => {
    if (!isFirstPage) {
      setCurrentSlide((prev) => prev - 1);
    }
  }, [isFirstPage]);

  const visibleTestimonials = useMemo(() => {
    if (!hasMultipleTestimonials) return testimonials;

    if (currentSlide === 0) {
      return testimonials.slice(0, 3);
    }

    const index = currentSlide + 2;
    return index < testimonials.length
      ? [testimonials[index]]
      : [testimonials[index % testimonials.length]];
  }, [hasMultipleTestimonials, currentSlide, testimonials]);

  return (
    <section
      className={`relative w-full ${data.name ? 'pt-800 pb-350' : 'pb-350'} px-500 overflow-hidden bg-[#EDF2F8] ${className}`}
    >
      <div className="relative z-10 mx-auto max-w-7xl">
        {data.name && (
          <div className="text-center mb-200">
            <h2 className="text-heading-200 font-bold leading-action tracking-[-0.03em] text-[#34363B] max-w-[600px] mx-auto">
              {data.name}
            </h2>
          </div>
        )}
        {data.features?.some((f) => f.buttons?.length) && (
          <div className="text-center">
            {data.features?.map((feature) => (
              <React.Fragment key={feature.heading}>
                {feature.buttons?.map((button, i) => (
                  <a
                    key={`${feature.heading}-button-${i}`}
                    href={button.url}
                    target={button.external ? '_blank' : '_self'}
                    rel={button.external ? 'noopener noreferrer' : undefined}
                    className="inline-flex items-center gap-2 text-[#0054A4] font-semibold hover:text-blue-700 text-base"
                  >
                    {button.text}
                    <LinkIcon
                      type={button.icon}
                      className={button.icon === 'ExternalIcon' ? 'w-4 h-4 ml-1' : 'w-5 h-5'}
                    />
                  </a>
                ))}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="relative">
          {hasMultipleTestimonials && (
            <>
              <button
                onClick={goToPrev}
                disabled={isFirstPage}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white hover:bg-gray-50 p-3 rounded-full shadow-lg transition-all z-10 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                aria-label="Previous testimonial"
              >
                <ChevronLeftIcon />
              </button>
              <button
                onClick={goToNext}
                disabled={isLastPage}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white hover:bg-gray-50 p-3 rounded-full shadow-lg transition-all z-10 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                aria-label="Next testimonial"
              >
                <ChevronRightIcon />
              </button>
            </>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-150">
            {visibleTestimonials.map((feature, index) => (
              <div
                key={`${feature.heading}-${index}`}
                className="bg-white rounded-400 p-200 flex flex-col justify-between transition-all duration-500"
              >
                <div>
                  <div className="mb-100">
                    <img src={quoteIcon} alt="quote" className="w-12 h-12" />
                  </div>
                  <p className="!text-body-150 !font-medium !leading-[1.63] text-black/90">
                    {feature.heading}
                  </p>
                </div>

                <div>
                  {feature.image?.url && (
                    <div className="mb-50">
                      <img
                        src={feature.image.url}
                        alt={feature.image.description ?? feature.author_name ?? 'Logo'}
                        className="h-11 w-auto object-contain"
                      />
                    </div>
                  )}
                  <div
                    className="!text-detail-90 !font-medium !leading-[1.38] !text-gray-800"
                    dangerouslySetInnerHTML={{ __html: feature.body }}
                  />
                </div>
              </div>
            ))}
          </div>

          {totalPages > 0 && (
            <div className="flex justify-center gap-2 mt-8 mb-16">
              {Array.from({ length: totalPages }, (_, index) => index).map((pageIndex) => (
                <button
                  key={`page-${pageIndex}`}
                  onClick={() => setCurrentSlide(pageIndex)}
                  className={`h-2 rounded-full transition-all ${
                    pageIndex === currentSlide ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'
                  }`}
                  aria-label={`Go to page ${pageIndex + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CmsJoinSection;
